import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/projects';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await authService.login(email, password);
      
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        setShowMFA(true);
        setIsLoading(false);
        return;
      }

      if (result.success && result.data) {
        login(result.data.user, result.data.accessToken);
        
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        const from = location.state?.from?.pathname || '/projects';
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Credenciales inválidas. Intenta nuevamente.');
      setIsLoading(false);
    }
  };

  const handleMFAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await authService.verifyMFA(mfaToken, mfaCode);
      
      if (result.success && result.data) {
        login(result.data.user, result.data.accessToken);
        
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        }
        
        const from = location.state?.from?.pathname || '/projects';
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Código MFA inválido');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Ingresa tu correo electrónico');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authService.forgotPassword(email);
      alert('Se ha enviado un enlace de recuperación a tu correo.');
    } catch (err: any) {
      setError(err.message || 'Error al enviar el enlace');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // PANTALLA MFA
  // ============================================================
  if (showMFA) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>Verificación de Dos Factores</h1>
            <p>Ingresa el código de 6 dígitos de tu aplicación de autenticación</p>
          </div>

          <form onSubmit={handleMFAVerify} className="login-form">
            <div className="form-group">
              <label htmlFor="mfaCode">Código MFA</label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
                style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
              />
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <span>{error}</span>
              </div>
            )}

            <button type="submit" className="btn-login" disabled={isLoading || mfaCode.length !== 6}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Verificando...
                </>
              ) : (
                'Verificar Código'
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowMFA(false);
                  setMfaCode('');
                  setMfaToken('');
                  setIsLoading(false);
                }}
                className="forgot-link"
              >
                ← Volver al inicio de sesión
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ============================================================
  // PANTALLA DE LOGIN
  // ============================================================
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0f172a',
      backgroundImage: 'radial-gradient(ellipse at top, #1a202c 0%, #0f172a 100%)',
      padding: '20px'
    }}>
      <div className="login-container" style={{ maxWidth: '420px', padding: 0 }}>
        <div className="login-box">
          <div className="login-header">
            <h1>Gran Arquitectura</h1>
            <p>Inicia sesión para acceder al sistema</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Correo electrónico</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                disabled={isLoading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
              />
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                />
                Recordarme
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                className="forgot-link"
                disabled={isLoading}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <span>{error}</span>
              </div>
            )}

            <button type="submit" className="btn-login" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </button>

            <div className="login-footer">
              <p>© 2026 Gran Arquitectura. Todos los derechos reservados.</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}